import { Mail, Users } from 'lucide-react'

import { UserAvatar } from '~/components/UserAvatar'
import { UserDisplayUser } from '~/components/component-library/UserDisplay/types'
import { SignerIcon } from '~/components/smart-accounts/SignerIcon'
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  HoverCardPortal,
} from '~/components/ui/hover-card'
import { useActiveOrg } from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import { OrgSigner } from '~/types'
import { useIsMobile } from '~/utils/devices'

export const UserPopover = ({
  user,
  optionalSigner,
  children,
  align = 'center',
}: {
  user: UserDisplayUser
  optionalSigner?: OrgSigner
  children: React.ReactNode
  align?: 'start' | 'center' | 'end'
}) => {
  const { org: activeOrg } = useActiveOrg()
  const { user: authUser } = useAuthUser()
  const isMobile = useIsMobile()

  // check for this user in the org members
  const orgMember = activeOrg?.members.find((member) => member.id === user.id)

  // don't render the popover if not in your org or on mobile
  if (!orgMember || isMobile) return children

  return (
    <HoverCard openDelay={500} closeDelay={100}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent
          avoidCollisions
          collisionPadding={8}
          side="top"
          align={align}
          className="grid w-auto max-w-64 grid-cols-[auto_1fr] gap-2.5 p-3"
        >
          <UserAvatar user={user} size={40} />
          <div className="flex w-full min-w-0 flex-col gap-2">
            <span className="flex items-center gap-1 text-sm font-medium leading-4 text-primary">
              <span className="truncate">
                {user?.displayName ?? user?.email}
              </span>
              {user.id === authUser?.id && (
                <span className="font-normal text-secondary"> (you)</span>
              )}
            </span>
            {[
              {
                icon: <Mail size={14} />,
                value: user?.email,
                hide: !user?.displayName || !user?.email,
              },
              {
                icon: <Users size={14} />,
                value: activeOrg!.name + ' ' + orgMember.role.toLowerCase(),
                hide: !orgMember,
              },
              {
                icon: (
                  <SignerIcon signer={optionalSigner as OrgSigner} size={14} />
                ),
                value: optionalSigner?.displayName,
                hide: !optionalSigner,
              },
            ]
              .filter((item) => !item.hide)
              .map((item) => (
                <div
                  key={item.value}
                  className="flex h-4 min-w-0 flex-1 items-start gap-1.5 text-sm leading-4 text-secondary"
                >
                  <div className="py-px">{item.icon}</div>
                  <span className="flex-1 truncate">{item.value}</span>
                </div>
              ))}
          </div>
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  )
}
